import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import {
  PIPELINE_STAGES, PHASES, PHASE_COLORS, countryFlag, currency, relativeTime, STAGE_BY_ID,
} from "@/lib/format";
import { Plus, LayoutGrid, List, Search, X } from "lucide-react";
import { toast } from "sonner";

export default function Leads({ role }) {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [view, setView] = useState("kanban"); // kanban | table
  const [search, setSearch] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("All");
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    const [l, h] = await Promise.all([api.get("/leads"), api.get("/hospitals")]);
    setLeads(l.data); setHospitals(h.data);
    if (role !== "partner") {
      try { const u = await api.get("/users"); setUsers(u.data); } catch {/* ignore */}
    }
  };

  useEffect(() => {
    (async () => {
      try { await reload(); }
      catch (e) { toast.error(getApiErrorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return leads.filter((l) => {
      if (s && ![l.patient_name, l.treatment, l.country, l.email].some((x) => (x || "").toLowerCase().includes(s))) return false;
      if (phaseFilter !== "All") {
        const stage = STAGE_BY_ID[l.stage];
        if (!stage || stage.phase !== phaseFilter) return false;
      }
      return true;
    });
  }, [leads, search, phaseFilter]);

  const updateStage = async (id, stage) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { stage });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      toast.success(`Moved to ${STAGE_BY_ID[stage]?.label}`);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  return (
    <div data-testid="leads-page">
      <PageHeader
        eyebrow="Pipeline"
        title="Leads"
        subtitle="24-stage IPMP pipeline — from first enquiry to closure."
        actions={
          <button
            onClick={() => setShowNew(true)}
            data-testid="new-lead-button"
            className="bg-[#2A4D3E] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#1E3B2F] hover:-translate-y-0.5 transition-all flex items-center gap-2"
          >
            <Plus size={15} /> New lead
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E9992]" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by patient, treatment, country…"
            data-testid="leads-search-input"
            className="w-full pl-9 pr-3 py-2.5 border border-[#E8E6E1] rounded-full bg-white text-sm focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]"
          />
        </div>
        <div className="flex gap-1 bg-white border border-[#E8E6E1] rounded-full p-1">
          {["All", ...PHASES].map((p) => (
            <button
              key={p} onClick={() => setPhaseFilter(p)}
              data-testid={`filter-phase-${p.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${phaseFilter === p ? "bg-[#2A4D3E] text-white" : "text-[#5C6660] hover:bg-[#F7F5F2]"}`}
            >
              {p}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-white border border-[#E8E6E1] rounded-full p-1">
          <button data-testid="view-kanban" onClick={() => setView("kanban")} className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition ${view === "kanban" ? "bg-[#2A4D3E] text-white" : "text-[#5C6660]"}`}>
            <LayoutGrid size={13} /> Kanban
          </button>
          <button data-testid="view-table" onClick={() => setView("table")} className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition ${view === "table" ? "bg-[#2A4D3E] text-white" : "text-[#5C6660]"}`}>
            <List size={13} /> Table
          </button>
        </div>
      </div>

      {loading ? <div className="text-sm text-[#5C6660]">Loading…</div> :
        view === "kanban" ? (
          <Kanban leads={filtered} role={role} onMove={updateStage} />
        ) : (
          <TableView leads={filtered} role={role} onMove={updateStage} />
        )}

      {showNew && <NewLeadModal onClose={() => setShowNew(false)} onCreated={async () => { setShowNew(false); await reload(); toast.success("Lead created"); }} hospitals={hospitals} users={users} role={role} />}
    </div>
  );
}

function Kanban({ leads, role, onMove }) {
  const grouped = useMemo(() => {
    const map = {};
    PIPELINE_STAGES.forEach((s) => (map[s.id] = []));
    leads.forEach((l) => { if (map[l.stage]) map[l.stage].push(l); });
    return map;
  }, [leads]);

  const onDrop = (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (id) onMove(id, stage);
  };

  return (
    <div className="flex gap-4 overflow-x-auto kanban-scroll pb-4 -mx-2 px-2" data-testid="leads-kanban">
      {PIPELINE_STAGES.map((s) => (
        <div
          key={s.id}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => onDrop(e, s.id)}
          data-testid={`kanban-column-${s.id}`}
          className="flex-shrink-0 w-72 bg-[#F2F0EC] rounded-2xl p-3"
        >
          <div className="flex items-center justify-between px-2 mb-3">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              <span className="text-xs font-semibold uppercase tracking-wider text-[#2A332C]">{s.label}</span>
            </div>
            <span className="text-xs text-[#8E9992]">{grouped[s.id].length}</span>
          </div>
          <div className="space-y-2 min-h-[80px]">
            {grouped[s.id].map((l) => (
              <KanbanCard key={l.id} lead={l} role={role} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function KanbanCard({ lead, role }) {
  const navigate = useNavigate();
  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/plain", lead.id)}
      onClick={() => navigate(`/${role}/leads/${lead.id}`)}
      data-testid={`lead-card-${lead.id}`}
      className="bg-white border border-[#E8E6E1] border-l-4 rounded-xl p-3 hover:shadow-md transition cursor-grab active:cursor-grabbing"
      style={{ borderLeftColor: STAGE_BY_ID[lead.stage]?.color || "#2A4D3E" }}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="text-sm font-semibold text-[#2A332C] truncate">{countryFlag(lead.country)} {lead.patient_name}</div>
      </div>
      <div className="text-[11px] text-[#5C6660] mb-2 line-clamp-2">{lead.treatment}</div>
      <div className="flex items-center justify-between text-[10px] text-[#8E9992]">
        <span>{currency(lead.estimated_value)}</span>
        <span>{relativeTime(lead.updated_at || lead.created_at)}</span>
      </div>
    </div>
  );
}

function TableView({ leads, role, onMove }) {
  const navigate = useNavigate();
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden" data-testid="leads-table">
      <table className="w-full">
        <thead className="bg-[#FDFCFB] border-b border-[#E8E6E1] text-xs font-semibold uppercase tracking-wider text-[#5C6660] text-left">
          <tr>
            <th className="px-5 py-3">Patient</th>
            <th className="px-5 py-3">Country</th>
            <th className="px-5 py-3">Treatment</th>
            <th className="px-5 py-3">Stage</th>
            <th className="px-5 py-3">Value</th>
            <th className="px-5 py-3">Updated</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} onClick={() => navigate(`/${role}/leads/${l.id}`)} data-testid={`lead-row-${l.id}`}
                className="border-b border-[#E8E6E1] last:border-0 hover:bg-[#F7F5F2] cursor-pointer transition">
              <td className="px-5 py-3.5 text-sm font-medium text-[#2A332C]">{l.patient_name}</td>
              <td className="px-5 py-3.5 text-sm text-[#5C6660]">{countryFlag(l.country)} {l.country || "—"}</td>
              <td className="px-5 py-3.5 text-sm text-[#5C6660]">{l.treatment}</td>
              <td className="px-5 py-3.5"><StatusBadge kind="stage" value={l.stage} /></td>
              <td className="px-5 py-3.5 text-sm text-[#2A332C] font-medium">{currency(l.estimated_value)}</td>
              <td className="px-5 py-3.5 text-xs text-[#8E9992]">{relativeTime(l.updated_at || l.created_at)}</td>
            </tr>
          ))}
          {leads.length === 0 && (
            <tr><td colSpan="6" className="px-5 py-10 text-center text-sm text-[#8E9992]">No leads match your filters.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function NewLeadModal({ onClose, onCreated, hospitals, users, role }) {
  const [form, setForm] = useState({
    patient_name: "", email: "", phone: "", country: "", treatment: "",
    notes: "", estimated_value: 0, preferred_hospital_id: "", coordinator_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const coordinators = users.filter((u) => u.role === "coordinator");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, estimated_value: parseFloat(form.estimated_value || 0) };
      ["preferred_hospital_id", "coordinator_id"].forEach((k) => { if (!payload[k]) delete payload[k]; });
      ["email", "phone", "country", "notes"].forEach((k) => { if (!payload[k]) delete payload[k]; });
      await api.post("/leads", payload);
      onCreated();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-2xl p-7 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="new-lead-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#8E9992] font-semibold mb-1">Capture</div>
            <h2 className="font-heading text-2xl font-semibold text-[#2A332C]">New lead</h2>
          </div>
          <button onClick={onClose} className="text-[#8E9992] hover:text-[#2A332C]"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <ModalField label="Patient name *" name="patient_name" value={form.patient_name} onChange={onChange} required testid="lead-name" />
          <ModalField label="Country" name="country" value={form.country} onChange={onChange} testid="lead-country" />
          <ModalField label="Email" type="email" name="email" value={form.email} onChange={onChange} testid="lead-email" />
          <ModalField label="Phone" name="phone" value={form.phone} onChange={onChange} testid="lead-phone" />
          <ModalField label="Treatment *" name="treatment" value={form.treatment} onChange={onChange} required testid="lead-treatment" />
          <ModalField label="Estimated value (USD)" type="number" name="estimated_value" value={form.estimated_value} onChange={onChange} testid="lead-value" />
          <ModalSelect label="Preferred hospital" name="preferred_hospital_id" value={form.preferred_hospital_id} onChange={onChange} testid="lead-hospital">
            <option value="">— None —</option>
            {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </ModalSelect>
          {role !== "partner" && (
            <ModalSelect label="Assign coordinator" name="coordinator_id" value={form.coordinator_id} onChange={onChange} testid="lead-coordinator">
              <option value="">— None —</option>
              {coordinators.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </ModalSelect>
          )}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-[#5C6660] mb-1.5">Notes</label>
            <textarea name="notes" value={form.notes} onChange={onChange} rows="3"
              data-testid="lead-notes"
              className="w-full border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] text-[#2A332C] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]" />
          </div>
          <div className="col-span-2 flex justify-end gap-2 mt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm text-[#5C6660] hover:text-[#2A332C]">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="submit-new-lead"
              className="bg-[#2A4D3E] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#1E3B2F] disabled:opacity-50">
              {submitting ? "Creating…" : "Create lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalField({ label, testid, ...rest }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#5C6660] mb-1.5">{label}</label>
      <input {...rest} data-testid={testid}
        className="w-full border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] text-[#2A332C] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]" />
    </div>
  );
}

function ModalSelect({ label, testid, children, ...rest }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#5C6660] mb-1.5">{label}</label>
      <select {...rest} data-testid={testid}
        className="w-full border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] text-[#2A332C] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]">
        {children}
      </select>
    </div>
  );
}
